import React, { useState, useEffect } from 'react';
import { format } from "date-fns";
import { TextField, Box, Button, 
    Dialog, DialogTitle, DialogContent, DialogActions, 
    FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { de, enGB } from 'date-fns/locale';
import FlexBetween from 'components/FlexBetween';


const parseHHmm = (str) => {
    if (!str) return null;
    const [h, m] = str.split(':').map(Number);
    const d = new Date();
    d.setHours(h, m, 0, 0);
    return d;
};

const FlightFilterDialog = ({
    open,
    onClose,
    onApply,
    acTypeOptions,
    flightRegOptions,
    sectorTypeOptions,
    departureTypeOptions,
    arrivalTypeOptions,
    currentFilters,
}) => {
    const locales = { 'en-us': undefined, 'en-gb': enGB, de };
    const [cleared, setCleared] = useState(false);

    useEffect(() => {
        if (cleared) {
            const timeout = setTimeout(() => {
                setCleared(false);
            }, 1500);

            return () => clearTimeout(timeout);
        }
        return () => {};
    }, [cleared]);

    const initialValues = {
        flightDate: null,
        flightNo: '',
        minEtd: null,
        maxEtd: null,
        sector: '',
        acType: [],
        flightReg: [],
        sectorType: 'ALL',
        departureType: 'ALL',
        arrivalType: 'ALL',
    };

    const [filterValues, setFilterValues] = useState(() => {
        if (!currentFilters) return initialValues;
        return {
            ...initialValues,
            ...currentFilters,
            acType: currentFilters.acType ?? [],
            flightReg: currentFilters.flightReg ?? [],
            sectorType: currentFilters.sectorType ?? 'ALL',
            departureType: currentFilters.departureType ?? 'ALL',
            arrivalType: currentFilters.arrivalType ?? 'ALL',
            minEtd: parseHHmm(currentFilters.minEtd),
            maxEtd: parseHHmm(currentFilters.maxEtd),
        };
    });

    const handleDateChange = (date) => {
        setFilterValues(prev => ({ ...prev, flightDate: date }));
    };

    const handleTimeChange = (field) => (time) => {
        setFilterValues(prev => ({ 
            ...prev, 
            [field]: time, // Date object
        }));
    };

    const handleFilterChange = (field) => (event) => {
        const value = event.target.value;
        setFilterValues(prev => ({
            ...prev,
            [field]: value, 
        }));
    };     

    const handleCancel = () => {
        setFilterValues(initialValues);  
        onApply(null);
        onClose();  
    };
    
    const handleApply = () => {
        const { sectorType, departureType, arrivalType } = filterValues;
    
        const comboFilters = {
            sectorType: sectorType === 'ALL' ? null : sectorType,
            departureType: departureType === 'ALL' ? null : departureType,
            arrivalType: arrivalType === 'ALL' ? null : arrivalType,
        };

        const appliedFilters = {
            ...filterValues,
            ...comboFilters,
            minEtd: filterValues.minEtd ? format(filterValues.minEtd, "HH:mm") : null,
            maxEtd: filterValues.maxEtd ? format(filterValues.maxEtd, "HH:mm") : null,
            acType: filterValues.acType.length ? filterValues.acType : null,
            flightReg: filterValues.flightReg.length ? filterValues.flightReg : null,
        };
        onApply(appliedFilters);
        onClose();
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleApply();
        }
    };

    return (   
        <LocalizationProvider 
            dateAdapter={AdapterDateFns}
            adapterLocale={locales['en-gb']}
        >     
            <Dialog 
                open={open} 
                onClose={handleCancel}
                sx={{ '& .MuiDialog-paper': { minWidth: '30%' } }}
            >
                <DialogTitle>
                    Filter
                </DialogTitle>
                <DialogContent onKeyDown={handleKeyPress}>
                    <Box display="flex" flexDirection="column" gap={2}>
                        <FlexBetween 
                            gap={2}
                            sx={{ marginTop: '6px' }}
                        >
                            <DatePicker
                                label="Flight Date"
                                value={filterValues.flightDate}
                                onChange={handleDateChange}
                                format="dd/MM/yyyy"
                                sx={{ width: '50%' }}
                                slotProps={{
                                    field: { clearable: true, onClear: () => setCleared(true) },
                                }}
                            />

                            <TextField
                                fullWidth
                                label="Flight No"
                                value={filterValues.flightNo}
                                onChange={handleFilterChange('flightNo')}
                                sx={{ width: '50%' }}
                            />
                        </FlexBetween>
                        
                        <FlexBetween gap={2}>                           
                            <TimePicker 
                                label="Min ETD"
                                views={['hours', 'minutes']} 
                                format="HH:mm"                                 
                                value={filterValues.minEtd}
                                onChange={handleTimeChange('minEtd')}
                                sx={{ width: '50%' }}
                                slotProps={{
                                    field: { clearable: true, onClear: () => setCleared(true) },
                                }}
                            />
                                     
                            <TimePicker 
                                label="Max ETD"
                                views={['hours', 'minutes']} 
                                format="HH:mm"                                 
                                value={filterValues.maxEtd}
                                onChange={handleTimeChange('maxEtd')}
                                sx={{ width: '50%' }}
                                slotProps={{
                                    field: { clearable: true, onClear: () => setCleared(true) },
                                }}
                            />        
                        </FlexBetween>

                        <FlexBetween gap={2}>         
                            <TextField
                                fullWidth
                                label="Sector"
                                value={filterValues.sector}
                                onChange={(event) => {
                                    const value = event.target.value.toUpperCase();
                                    setFilterValues((prev) => ({ ...prev, sector: value }));
                                }}
                            />  
                            {sectorTypeOptions && (
                                <FormControl fullWidth>
                                    <InputLabel>Sector Type</InputLabel>
                                    <Select
                                        label="Sector Type"
                                        value={filterValues.sectorType}
                                        onChange={handleFilterChange('sectorType')}
                                    >
                                        <MenuItem value="ALL">ALL</MenuItem>
                                        {sectorTypeOptions.map((type, index) => (
                                            <MenuItem key={index} value={type}>
                                                {type}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl> 
                            )}                                                 
                        </FlexBetween>   

                        <FlexBetween gap={2}>         
                            {departureTypeOptions && (
                                <FormControl fullWidth>
                                    <InputLabel>Departure Type</InputLabel>
                                    <Select
                                        label="Departure Type"
                                        value={filterValues.departureType}
                                        onChange={handleFilterChange('departureType')}
                                    >
                                        <MenuItem value="ALL">ALL</MenuItem>
                                        {departureTypeOptions.map((type, index) => (
                                            <MenuItem key={index} value={type}>
                                                {type}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl> 
                            )}
                            {arrivalTypeOptions && (
                                <FormControl fullWidth>
                                    <InputLabel>Arrival Type</InputLabel>
                                    <Select
                                        label="Arrival Type"
                                        value={filterValues.arrivalType}
                                        onChange={handleFilterChange('arrivalType')}
                                    >
                                        <MenuItem value="ALL">ALL</MenuItem>
                                        {arrivalTypeOptions.map((type, index) => (
                                            <MenuItem key={index} value={type}>
                                                {type}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl> 
                            )}                                                 
                        </FlexBetween>

                        <FlexBetween gap={2}>                            
                            <FormControl fullWidth>
                                <InputLabel>AC Type</InputLabel>
                                <Select
                                    multiple
                                    label="AC Type"
                                    value={filterValues.acType}
                                    onChange={handleFilterChange('acType')}
                                    renderValue={(selected) => selected.join(', ')}
                                >
                                    {acTypeOptions.map((type, index) => (
                                        <MenuItem key={index} value={type}>
                                            {type}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl fullWidth>
                                <InputLabel>Flight Reg</InputLabel>
                                <Select
                                    multiple
                                    label="Flight Reg"
                                    value={filterValues.flightReg}
                                    onChange={handleFilterChange('flightReg')}
                                    renderValue={(selected) => selected.join(', ')}
                                >
                                    {flightRegOptions.map((reg, index) => (
                                        <MenuItem key={index} value={reg}>
                                            {reg}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>                                        
                        </FlexBetween>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button 
                        variant="outlined" 
                        color="secondary" 
                        onClick={handleCancel} 
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="outlined"
                        onClick={handleApply} 
                    >
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>
        </LocalizationProvider>
    );
};

export default FlightFilterDialog;
